import { Verified as VerifiedIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  PimProductAttributionKind,
  PimProductPreferenceScope,
} from "../../../../clients/graphqlTypes.js";
import type { ProductsListViewProduct } from "./ProductItem.js";

/** Brands before articles, a product type's brand preference before a global one. */
const RANKING_SOURCES = [
  { kind: "BRAND", global: false },
  { kind: "BRAND", global: true },
  { kind: "ARTICLE_NUMBER", global: false },
] as const;
const RANKING_SCOPES = ["USER", "ORGANISATION"] as const;

type RankingSource = (typeof RANKING_SOURCES)[number];

export type PreferenceRanking = RankingSource & {
  scope: PimProductPreferenceScope;
};

/**
 * The distinct preferences PIM attributes the hit's rank to, ordered by source, then scope.
 * A brand preference without a product type is a global one. Only search results carry
 * attributions; any other product shape yields none.
 */
export function preferenceRankings(
  product: ProductsListViewProduct
): PreferenceRanking[] {
  if (!("attributions" in product)) return [];
  const attributions: ReadonlyArray<{
    kind: PimProductAttributionKind;
    scope: PimProductPreferenceScope;
    productType?: { productTypeKey: string } | null;
  }> = product.attributions;
  return RANKING_SOURCES.flatMap(source =>
    RANKING_SCOPES.filter(scope =>
      attributions.some(
        attribution =>
          attribution.kind === source.kind &&
          attribution.scope === scope &&
          (attribution.productType == null) === source.global
      )
    ).map(scope => ({ ...source, scope }))
  );
}

/** Read-only: names every preference that ranks the hit and whose it is. */
export function PreferenceRankingMarker({
  rankings,
}: {
  rankings: PreferenceRanking[];
}) {
  const { t } = useTranslate(["Product"]);

  const labelOf = ({ kind, global, scope }: PreferenceRanking) => {
    switch (kind) {
      case "BRAND":
        if (global)
          return scope === "USER"
            ? t("Your global brand preference", { ns: "Product" })
            : t("Your organisation's global brand preference", {
                ns: "Product",
              });
        return scope === "USER"
          ? t("Your brand preference", { ns: "Product" })
          : t("Your organisation's brand preference", { ns: "Product" });
      case "ARTICLE_NUMBER":
        return scope === "USER"
          ? t("Your article preference", { ns: "Product" })
          : t("Your organisation's article preference", { ns: "Product" });
    }
  };

  const heading = t("Ranked higher by:", { ns: "Product" });
  const lines = rankings.map(labelOf);

  return (
    <Tooltip
      title={
        <>
          {heading}
          {lines.map(line => (
            <div key={line}>{line}</div>
          ))}
        </>
      }
    >
      <VerifiedIcon
        aria-label={`${heading} ${lines.join(", ")}`}
        role="img"
        fontSize="small"
        color="primary"
      />
    </Tooltip>
  );
}
